import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Filter, RefreshCw, Download, ExternalLink, AlertTriangle, Clock, Moon,
  Archive, RotateCcw, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Plus, FlaskConical, X, Beaker,
} from "lucide-react";
import {
  OFFER_LABELS, STATUSES, PRIORITIES, statusMeta, priorityMeta,
  isStale, isSnoozed, formatSnoozeUntil, formatDate, normalizeStatus,
} from "./leadHelpers";

const ONBOARDING_REQUIRED_LABELS = {
  personal_id: "Personal ID",
  ssn: "Social Security",
  business_tax_id: "Business Tax ID/EIN",
  voided_check: "Voided Check",
  personal_phone: "Personal Phone",
  business_phone: "Business Phone",
  email: "Email",
};
function missingOnboardingItems(lead) {
  const info = lead?.onboarding_info || {};
  return Object.entries(ONBOARDING_REQUIRED_LABELS)
    .filter(([k]) => {
      const s = info[k]?.status;
      return s !== "received" && s !== "not_applicable";
    })
    .map(([, label]) => label);
}

/* Quick filter chips — the "always visible" 4 stay in the top row,
 * the rest collapse behind a "More Filters" toggle so the chip row
 * never overflows. */
const PRIMARY_FILTERS = [
  { key: "all", label: "All Leads" },
  { key: "new", label: "New" },
  { key: "follow_up", label: "Needs Follow-Up" },
  { key: "high_value", label: "High Value" },
];

// Order requested by admin: hot → due today → overdue → upcoming appts →
// missing onboarding → uploaded statement → statement needed → snoozed →
// duplicates.
const MORE_FILTERS = [
  { key: "hot_today", label: "Hot Leads" },
  { key: "followups_due_today", label: "Follow-Ups Today" },
  { key: "followups_overdue", label: "Overdue Follow-Ups" },
  { key: "appointments_upcoming", label: "Upcoming Appointments" },
  { key: "missing_onboarding", label: "Missing Onboarding Items" },
  { key: "has_statement", label: "Uploaded Statement" },
  { key: "info_needed", label: "Info Needed" },
  { key: "snoozed", label: "Snoozed" },
  { key: "duplicates", label: "Possible Duplicates" },
];

function PageSelectorBar({ page, totalPages, pageSize, onChangePage, onChangePageSize, total }) {
  return (
    <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3" data-testid="leads-pagination">
      <div className="text-[12.5px] text-slate-500">
        Showing <span className="font-semibold text-slate-700">{Math.min(total, (page - 1) * pageSize + 1)}</span>–
        <span className="font-semibold text-slate-700">{Math.min(total, page * pageSize)}</span> of{" "}
        <span className="font-semibold text-slate-700">{total}</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[12.5px] text-slate-500">
          Per page:
          <select
            value={pageSize}
            onChange={(e) => onChangePageSize(Number(e.target.value))}
            data-testid="leads-pagesize"
            className="ml-1.5 px-2 py-1 rounded-md border border-slate-200 text-[12.5px]"
          >
            {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => onChangePage(Math.max(1, page - 1))}
          disabled={page <= 1}
          data-testid="leads-prev-page"
          className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md border border-slate-200 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Prev
        </button>
        <span className="text-[12.5px] font-semibold text-slate-700" data-testid="leads-page-indicator">
          Page {page} of {Math.max(1, totalPages)}
        </span>
        <button
          type="button"
          onClick={() => onChangePage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          data-testid="leads-next-page"
          className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md border border-slate-200 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function ViewToggle({ view, onChangeView, stats }) {
  const views = [
    { v: "active", label: "Active", count: null },
    { v: "archived", label: "Archived", count: stats.archived },
    { v: "trash", label: "Trash", count: stats.trash },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="view-toggle">
      {views.map((vw) => {
        const active = view === vw.v;
        return (
          <button
            key={vw.v}
            type="button"
            onClick={() => onChangeView(vw.v)}
            data-testid={`view-${vw.v}`}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[13px] font-bold border transition-colors ${
              active
                ? (vw.v === "trash"
                    ? "bg-rose-600 text-white border-rose-600"
                    : vw.v === "archived"
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-[var(--swipe-navy)] text-white border-[var(--swipe-navy)]")
                : "bg-white text-slate-700 border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)]"
            }`}
          >
            {vw.label}
            {vw.count !== null ? (
              <span className={`inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full px-1.5 text-[10.5px] font-bold ${
                active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-600"
              }`}>{vw.count}</span>
            ) : null}
          </button>
        );
      })}
      {view !== "active" ? (
        <span className={`ml-1 text-[12px] font-semibold px-3 py-1 rounded-full ${
          view === "trash" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"
        }`}>
          Showing {view === "trash" ? "trashed" : "archived"} leads — excluded from default views, stats, and chart.
        </span>
      ) : null}
    </div>
  );
}

function QuickFilterChip({ f, active, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`chip-${f.key}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold border whitespace-nowrap transition-colors ${
        active
          ? "bg-[var(--swipe-navy)] text-white border-[var(--swipe-navy)]"
          : "bg-white text-slate-700 border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)]"
      }`}
    >
      {f.label}
      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full px-1.5 text-[10.5px] font-bold ${
        active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-600"
      }`}>{count}</span>
    </button>
  );
}

function BulkActionBar({ count, view, onClear, onBulkArchive, onBulkUnarchive, onBulkTrash, onBulkRestore, onBulkPermanentDelete }) {
  if (count === 0) return null;
  return (
    <div
      data-testid="bulk-action-bar"
      className="sticky top-[112px] z-20 mb-3 rounded-xl bg-[var(--swipe-navy)] text-white border border-[var(--swipe-navy)] shadow-[0_10px_30px_-12px_rgba(6,26,58,0.45)] px-4 py-2.5 flex items-center gap-3 flex-wrap"
    >
      <span className="text-[13.5px] font-bold">
        {count} selected
      </span>
      <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/55">
        Bulk actions
      </span>
      <div className="flex items-center gap-2 flex-wrap ml-auto">
        {view === "active" ? (
          <>
            <button
              type="button"
              onClick={onBulkArchive}
              data-testid="bulk-archive"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-semibold"
            >
              <Archive className="w-4 h-4" /> Archive
            </button>
            <button
              type="button"
              onClick={onBulkTrash}
              data-testid="bulk-trash"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold"
            >
              <Trash2 className="w-4 h-4" /> Move to Trash
            </button>
          </>
        ) : null}
        {view === "archived" ? (
          <>
            <button
              type="button"
              onClick={onBulkUnarchive}
              data-testid="bulk-unarchive"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-[13px] font-semibold"
            >
              <RotateCcw className="w-4 h-4" /> Unarchive
            </button>
            <button
              type="button"
              onClick={onBulkTrash}
              data-testid="bulk-trash"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold"
            >
              <Trash2 className="w-4 h-4" /> Move to Trash
            </button>
          </>
        ) : null}
        {view === "trash" ? (
          <>
            <button
              type="button"
              onClick={onBulkRestore}
              data-testid="bulk-restore"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-[13px] font-semibold"
            >
              <RotateCcw className="w-4 h-4" /> Restore
            </button>
            <button
              type="button"
              onClick={onBulkPermanentDelete}
              data-testid="bulk-permanent-delete"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold"
            >
              <Trash2 className="w-4 h-4" /> Permanently Delete
            </button>
          </>
        ) : null}
        <button
          type="button"
          onClick={onClear}
          data-testid="bulk-clear"
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-white/20 text-white/85 hover:bg-white/10 text-[13px] font-semibold"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-3 text-left font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "", title }) {
  return <td className={`px-3 py-3 ${className}`} title={title}>{children}</td>;
}

export default function LeadsTab({
  /* state */
  leads, // already filtered by source/month upstream
  stats,
  view,
  busy,
  q, offer, status, sourceFilter, dateFrom, dateTo, quickFilter,
  sourceOptions,
  activeMonth,
  showTestLeads,
  /* setters */
  onSetView,
  onSetQ, onSetOffer, onSetStatus, onSetSourceFilter, onSetDateFrom, onSetDateTo, onSetQuickFilter,
  onClearActiveMonth,
  onClearAllFilters,
  onToggleShowTestLeads,
  /* actions */
  onRefresh,
  onExportCsv,
  onUpdateLead,
  onSnoozeLead,
  onUnsnoozeLead,
  onBulkArchive, onBulkUnarchive, onBulkTrash, onBulkRestore, onBulkPermanentDelete,
  onCreateManualLead,
  chipCount, // function (key) => count
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState(() => new Set());
  const [manualOpen, setManualOpen] = useState(false);

  // Reset page + selection whenever the filters/view change (defensive).
  // setPage/setSelected are stable React setters — listing them silences the
  // exhaustive-deps lint without changing runtime behavior.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [view, q, offer, status, sourceFilter, dateFrom, dateTo, quickFilter, activeMonth, setPage, setSelected]);

  const total = leads.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Clamp page if leads list shrank past the current page
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const pagedLeads = useMemo(() => {
    const start = (page - 1) * pageSize;
    return leads.slice(start, start + pageSize);
  }, [leads, page, pageSize]);

  const allOnPageSelected = pagedLeads.length > 0 && pagedLeads.every((l) => selected.has(l.id));
  const someOnPageSelected = pagedLeads.some((l) => selected.has(l.id));

  const togglePageSelection = () => {
    const next = new Set(selected);
    if (allOnPageSelected) {
      for (const l of pagedLeads) next.delete(l.id);
    } else {
      for (const l of pagedLeads) next.add(l.id);
    }
    setSelected(next);
  };

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const runBulk = async (fn) => {
    if (!fn) return;
    const ids = Array.from(selected);
    if (!ids.length) return;
    await fn(ids);
    setSelected(new Set());
  };

  const hasFilters =
    q || offer !== "all" || status !== "all" || sourceFilter !== "all" || dateFrom || dateTo || activeMonth;

  return (
    <div data-testid="leads-tab" className="space-y-4">
      {/* View toggle */}
      <ViewToggle
        view={view}
        onChangeView={(v) => { onSetView(v); onSetQuickFilter("all"); }}
        stats={stats}
      />

      {/* Active month chip */}
      {activeMonth ? (
        <div className="flex items-center gap-2" data-testid="active-month-chip">
          <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[var(--swipe-navy)] bg-[var(--swipe-soft-blue)] border border-[var(--swipe-accent)]/30 rounded-full px-3 py-1">
            Filtered by month: {activeMonth}
            <button type="button" onClick={onClearActiveMonth} className="text-[var(--swipe-accent)] hover:underline">clear</button>
          </span>
        </div>
      ) : null}

      {/* Primary filter chips + More Filters toggle */}
      <div className="flex items-center flex-wrap gap-2" data-testid="quick-filters">
        {PRIMARY_FILTERS.map((f) => (
          <QuickFilterChip
            key={f.key}
            f={f}
            active={quickFilter === f.key}
            count={chipCount(f.key)}
            onClick={() => onSetQuickFilter(quickFilter === f.key && f.key !== "all" ? "all" : f.key)}
          />
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          data-testid="more-filters-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold border whitespace-nowrap transition-colors ${
            moreOpen
              ? "bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] border-[var(--swipe-accent)]/30"
              : "bg-white text-slate-700 border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)]"
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          More Filters
          {moreOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {moreOpen ? (
        <div
          data-testid="more-filters-panel"
          className="rounded-xl border border-slate-200 bg-white p-3"
        >
          <div className="flex items-center flex-wrap gap-2">
            {MORE_FILTERS.map((f) => (
              <QuickFilterChip
                key={f.key}
                f={f}
                active={quickFilter === f.key}
                count={chipCount(f.key)}
                onClick={() => onSetQuickFilter(quickFilter === f.key ? "all" : f.key)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Filters bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              value={q}
              onChange={(e) => onSetQ(e.target.value)}
              placeholder="Search business, name, phone, email…"
              data-testid="admin-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/15 outline-none text-sm"
            />
          </div>
          <select
            value={offer}
            onChange={(e) => onSetOffer(e.target.value)}
            data-testid="admin-filter-offer"
            className="md:col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm"
          >
            <option value="all">All lead types</option>
            {Object.entries(OFFER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select
            value={status}
            onChange={(e) => onSetStatus(e.target.value)}
            data-testid="admin-filter-status"
            className="md:col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => onSetSourceFilter(e.target.value)}
            data-testid="admin-filter-source"
            className="md:col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm"
          >
            <option value="all">All sources</option>
            {sourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onSetDateFrom(e.target.value)}
            data-testid="admin-filter-date-from"
            className="md:col-span-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onSetDateTo(e.target.value)}
            data-testid="admin-filter-date-to"
            className="md:col-span-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
          />
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={onRefresh}
            disabled={busy}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={onExportCsv}
            disabled={leads.length === 0}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-slate-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button
            type="button"
            onClick={() => setManualOpen(true)}
            data-testid="manual-add-lead-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--swipe-navy)] hover:bg-[#0b2654] text-white text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Add Lead
          </button>
          <label
            data-testid="show-test-leads-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-sm cursor-pointer hover:border-[var(--swipe-accent)]"
          >
            <input
              type="checkbox"
              checked={!!showTestLeads}
              onChange={onToggleShowTestLeads}
              className="w-4 h-4 rounded border-slate-300 text-[var(--swipe-accent)] focus:ring-[var(--swipe-accent)]"
            />
            <FlaskConical className="w-3.5 h-3.5" /> Show test leads
          </label>
          {hasFilters ? (
            <button
              onClick={onClearAllFilters}
              data-testid="admin-clear-filters"
              className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-600"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {/* Bulk action bar */}
      <BulkActionBar
        count={selected.size}
        view={view}
        onClear={() => setSelected(new Set())}
        onBulkArchive={() => runBulk(onBulkArchive)}
        onBulkUnarchive={() => runBulk(onBulkUnarchive)}
        onBulkTrash={() => runBulk(onBulkTrash)}
        onBulkRestore={() => runBulk(onBulkRestore)}
        onBulkPermanentDelete={() => runBulk(onBulkPermanentDelete)}
      />

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col style={{ width: "44px" }} />{/* checkbox */}
            <col style={{ width: "10%" }} />{/* Date */}
            <col style={{ width: "18%" }} />{/* Business */}
            <col style={{ width: "14%" }} />{/* Contact */}
            <col style={{ width: "11%" }} />{/* Lead Type */}
            <col style={{ width: "17%" }} />{/* Monthly Vol — wider so $100,000 – $250,000 fits */}
            <col style={{ width: "9%" }} />{/* Priority */}
            <col style={{ width: "9%" }} />{/* Status */}
            <col style={{ width: "11%" }} />{/* Actions */}
          </colgroup>
          <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                  onChange={togglePageSelection}
                  data-testid="select-all-checkbox"
                  className="w-4 h-4 rounded border-slate-300 text-[var(--swipe-accent)] focus:ring-[var(--swipe-accent)]"
                />
              </th>
              <Th>Date</Th>
              <Th>Business</Th>
              <Th>Contact</Th>
              <Th>Lead Type</Th>
              <Th>Monthly Vol.</Th>
              <Th>Priority</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pagedLeads.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-16 text-slate-400">No leads match these filters.</td></tr>
            ) : pagedLeads.map((l) => {
              const sm = statusMeta(l.status);
              const pm = priorityMeta(l.priority);
              const stale = isStale(l);
              const snoozed = isSnoozed(l);
              const isDup = (l.possible_duplicate_of || []).length > 0;
              const isSel = selected.has(l.id);
              return (
                <tr
                  key={l.id}
                  className={`hover:bg-slate-50 ${isSel ? "bg-[var(--swipe-accent-soft)]/50" : snoozed ? "bg-indigo-50/30" : stale ? "bg-rose-50/30" : ""}`}
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleRow(l.id)}
                      data-testid={`row-checkbox-${l.id}`}
                      className="w-4 h-4 rounded border-slate-300 text-[var(--swipe-accent)] focus:ring-[var(--swipe-accent)]"
                    />
                  </td>
                  <Td className="whitespace-nowrap text-slate-500 text-[12px]">
                    <div>{formatDate(l.created_at)}</div>
                    {stale ? (
                      <span data-testid={`stale-${l.id}`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-rose-700 bg-rose-100">
                        <AlertTriangle className="w-3 h-3" /> Stale Lead
                      </span>
                    ) : null}
                    {snoozed ? (
                      <span data-testid={`snoozed-${l.id}`} title={`Snoozed until ${formatSnoozeUntil(l.snooze_until)}`}
                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-indigo-700 bg-indigo-100">
                        <Moon className="w-3 h-3" /> Snoozed
                      </span>
                    ) : null}
                    {isDup ? (
                      <span data-testid={`dup-${l.id}`} title={`${l.possible_duplicate_of.length} possible duplicate(s)`}
                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-700 bg-amber-100">
                        <AlertTriangle className="w-3 h-3" /> Duplicate
                      </span>
                    ) : null}
                  </Td>
                  <Td className="font-semibold text-[var(--swipe-navy)] truncate" title={l.business_name || ""}>
                    <div className="flex items-center gap-1.5">
                      <span className="truncate">{l.business_name || "—"}</span>
                      {l.is_test_lead ? (
                        <span data-testid={`test-badge-${l.id}`} title="Test lead — excluded from dashboard metrics"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-fuchsia-700 bg-fuchsia-100 whitespace-nowrap">
                          <Beaker className="w-3 h-3" /> TEST
                        </span>
                      ) : null}
                    </div>
                    {quickFilter === "missing_onboarding" ? (() => {
                      const missing = missingOnboardingItems(l);
                      return missing.length ? (
                        <div className="mt-1 text-[10.5px] font-medium text-amber-800 truncate" title={`Missing: ${missing.join(", ")}`} data-testid={`missing-onboarding-${l.id}`}>
                          Missing: {missing.slice(0, 3).join(", ")}{missing.length > 3 ? `, +${missing.length - 3} more` : ""}
                        </div>
                      ) : null;
                    })() : null}
                  </Td>
                  <Td className="truncate" title={l.contact_name || ""}>{l.contact_name || "—"}</Td>
                  <Td>{OFFER_LABELS[l.offer_type] || l.offer_type}</Td>
                  <Td className="whitespace-nowrap text-[13px]" title={l.monthly_volume || ""}>
                    {l.monthly_volume || (l.monthly_volume_amount ? `$${Number(l.monthly_volume_amount).toLocaleString()}` : "—")}
                  </Td>
                  <Td>
                    <select
                      value={l.priority || "warm"}
                      onChange={(e) => onUpdateLead(l.id, { priority: e.target.value })}
                      data-testid={`lead-priority-${l.id}`}
                      className={`w-full text-[11px] font-semibold px-2 py-1 rounded-full border ${pm.color} outline-none cursor-pointer`}
                    >
                      {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                    </select>
                  </Td>
                  <Td>
                    <select
                      value={normalizeStatus(l.status)}
                      onChange={(e) => onUpdateLead(l.id, { status: e.target.value })}
                      data-testid={`lead-status-${l.id}`}
                      className={`w-full text-[11px] font-semibold px-2 py-1 rounded-full border ${sm.color} outline-none cursor-pointer`}
                    >
                      {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <Link
                        to={`/admin/leads/${l.id}`}
                        data-testid={`view-${l.id}`}
                        className="px-3 h-8 rounded-lg bg-[var(--swipe-accent)] hover:bg-[var(--swipe-accent)]/90 text-white text-[12px] font-bold inline-flex items-center gap-1 shadow-sm hover:shadow transition-all"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> View
                      </Link>
                      {snoozed ? (
                        <button
                          onClick={() => onUnsnoozeLead(l.id)}
                          title="Cancel snooze"
                          data-testid={`unsnooze-${l.id}`}
                          className="w-8 h-8 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 inline-flex items-center justify-center"
                        >
                          <Moon className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => onSnoozeLead(l.id)}
                          title="Snooze 24h"
                          data-testid={`snooze-${l.id}`}
                          className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 inline-flex items-center justify-center text-slate-600"
                        >
                          <Clock className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PageSelectorBar
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          total={total}
          onChangePage={setPage}
          onChangePageSize={(n) => { setPageSize(n); setPage(1); }}
        />
      </div>

      <ManualAddLeadModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreate={onCreateManualLead}
      />
    </div>
  );
}

/* ─── Manual Add Lead modal ──────────────────────────────────────────────── */
function ManualAddLeadModal({ open, onClose, onCreate }) {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", phone: "", email: "",
    offer_type: "contact", source_label: "Manual Entry",
    monthly_volume: "", current_processor: "", current_pos: "",
    notes: "", priority: "warm", status: "new",
  });
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onCreate({
        ...form,
        // Drop empty strings so backend stores null.
        business_name: form.business_name || null,
        contact_name: form.contact_name || null,
        phone: form.phone || null,
        email: form.email || null,
        monthly_volume: form.monthly_volume || null,
        current_processor: form.current_processor || null,
        current_pos: form.current_pos || null,
        notes: form.notes || null,
      });
      onClose();
    } catch { /* parent toast handles error */ }
    finally { setSubmitting(false); }
  };
  const FIELD = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[var(--swipe-accent)] focus:ring-2 focus:ring-[var(--swipe-accent)]/15 outline-none";
  return (
    <div
      onClick={onClose}
      data-testid="manual-add-lead-modal"
      className="fixed inset-0 z-50 bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-[0_30px_60px_-15px_rgba(6,26,58,0.45)] w-full max-w-lg max-h-[88vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[18px] font-extrabold text-[var(--swipe-navy)]">Add Lead Manually</h3>
          <button type="button" onClick={onClose} data-testid="manual-add-close"
            className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto">
          <div className="sm:col-span-2">
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Business name</label>
            <input className={FIELD} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="manual-business-name" placeholder="e.g. Corner Café" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Contact name</label>
            <input className={FIELD} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="manual-contact-name" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Phone</label>
            <input className={FIELD} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="manual-phone" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Email</label>
            <input type="email" className={FIELD} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="manual-email" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Lead type</label>
            <select className={FIELD} value={form.offer_type} onChange={(e) => set("offer_type", e.target.value)} data-testid="manual-offer-type">
              {Object.entries(OFFER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Source</label>
            <select className={FIELD} value={form.source_label} onChange={(e) => set("source_label", e.target.value)} data-testid="manual-source">
              <option value="Manual Entry">Manual Entry</option>
              <option value="Referral">Referral</option>
              <option value="Phone Call">Phone Call</option>
              <option value="Walk-In">Walk-In</option>
              <option value="Text Message">Text Message</option>
              <option value="Email">Email</option>
              <option value="Direct / Unknown">Direct / Unknown</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Monthly volume</label>
            <input className={FIELD} value={form.monthly_volume} onChange={(e) => set("monthly_volume", e.target.value)} data-testid="manual-monthly-volume" placeholder="e.g. $25,000 – $50,000" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Current processor</label>
            <input className={FIELD} value={form.current_processor} onChange={(e) => set("current_processor", e.target.value)} data-testid="manual-current-processor" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Current POS</label>
            <input className={FIELD} value={form.current_pos} onChange={(e) => set("current_pos", e.target.value)} data-testid="manual-current-pos" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Priority</label>
            <select className={FIELD} value={form.priority} onChange={(e) => set("priority", e.target.value)} data-testid="manual-priority">
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Status</label>
            <select className={FIELD} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="manual-status">
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Notes</label>
            <textarea rows={3} className={FIELD} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="manual-notes" />
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="manual-add-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] disabled:opacity-60 text-white text-[13px] font-semibold"
          >
            <Plus className="w-3.5 h-3.5" />
            {submitting ? "Adding…" : "Add Lead"}
          </button>
        </div>
      </form>
    </div>
  );
}
